import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IGoogleAdsAdText } from '@feathr/blackbox';
import { CardV2 as Card, Label, List, ListItem, Text } from '@feathr/components';

import AdPreview from '../../AdStep/AdPreview';
import type { IAd, ISitelink } from '../../AdStep/AdStep.types';

import * as styles from '../ReviewStep.css';

interface IAdReviewCardProps {
  readonly ad: IAd;
  readonly adNumber: number;
  /** Base for this card's `data-name` and those of its sections. */
  readonly name: string;
  /** The ad group's sitelinks — every ad in the group shows them in its preview. */
  readonly sitelinks: ISitelink[];
  /** The advertiser name Google shows above the display URL. */
  readonly businessName?: string;
}

/** Blank rows are placeholders the editor seeds, so they are not part of the ad. */
function getFilled(items: readonly IGoogleAdsAdText[]): IGoogleAdsAdText[] {
  return items.filter((item) => !!item.text.trim());
}

/**
 * One ad as it reads on the Review step: every asset it carries, then a preview of how one
 * combination of them will appear.
 *
 * The design shows a single "Ad Text" card, but a Search ad group holds two or more ads, so
 * each gets its own numbered card — matching how the Ads step labels them. The final URL is
 * also absent from the design and included here, since Search sets the landing page per ad
 * rather than per campaign.
 *
 * @param props - {@link IAdReviewCardProps}.
 */
function AdReviewCard({
  ad,
  adNumber,
  name,
  sitelinks,
  businessName,
}: Readonly<IAdReviewCardProps>): JSX.Element {
  const { t } = useTranslation();
  const headlines = getFilled(ad.headlines);
  const descriptions = getFilled(ad.descriptions);
  const finalUrl = ad.finalUrl.trim();
  const nothingYet = <Text.SmallRegular>{t('None yet.')}</Text.SmallRegular>;

  return (
    <Card name={name} width={'full'}>
      {/* Compact and an h4, because this sits inside the ad group's own card. */}
      <Card.Header padding={'compact'} title={t('Ad {{adNumber}}', { adNumber })} titleEl={'h4'} />
      <Card.Content addVerticalGap={true}>
        <div data-name={`${name}-headlines`}>
          <Label>{t('Headlines')}</Label>
          {headlines.length ? (
            <List hasBullets={true}>
              {headlines.map((headline) => (
                <ListItem key={headline.key}>{headline.text.trim()}</ListItem>
              ))}
            </List>
          ) : (
            nothingYet
          )}
        </div>
        <div data-name={`${name}-descriptions`}>
          <Label>{t('Descriptions')}</Label>
          {descriptions.length ? (
            <List hasBullets={true}>
              {descriptions.map((description) => (
                <ListItem key={description.key}>{description.text.trim()}</ListItem>
              ))}
            </List>
          ) : (
            nothingYet
          )}
        </div>
        <div data-name={`${name}-final-url`}>
          <Label>{t('Final URL')}</Label>
          {finalUrl ? (
            <Text.SmallRegular className={styles.url} truncate={false}>
              {finalUrl}
            </Text.SmallRegular>
          ) : (
            nothingYet
          )}
        </div>
      </Card.Content>
      {/*
       * Below the assets, since it shows one combination of them rather than all — the
       * lists above are what the ad actually holds.
       */}
      <Card.Content>
        <Label>{t('Preview')}</Label>
        <AdPreview
          ad={ad}
          businessName={businessName}
          name={`${name}-preview`}
          sitelinks={sitelinks}
        />
      </Card.Content>
    </Card>
  );
}

export default AdReviewCard;
